import { validate } from 'cnpj';
import BaseCadastro from "components/BaseCadastro";
import { useToastContext } from "components/alert/ToastContext";
import { Label } from 'components/input/Label';
import CPF from 'cpf-check';
import Pessoa from "model/administracao/cadastro/Pessoa";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from "primereact/inputtext";
import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PessoaService from "service/administracao/cadastro/PessoaService";
import { cepMask, cnpjMask, cpfMask, phoneMask } from "utils/mask";
import { Controller, useForm } from 'react-hook-form';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { TabPanel, TabView } from 'primereact/tabview';
import PessoaEndereco from 'model/fiscal/PessoaEndereco';

const pessoaService = new PessoaService();

type Props = {
    isModal?: boolean;
    onSave?: (model: Pessoa) => void;
}

export default function PessoaCadastro(props: Props) {

    const { register, control, handleSubmit, setFocus, clearErrors, setError, formState: { errors } } = useForm();

    const inputNomeRef = useRef<any>(null);

    const [queryParameters] = useSearchParams();

    const [pessoa, setPessoa] = useState<Pessoa>(new Pessoa());
    const [titulo, setTitulo] = useState<string>('Pessoa');

    const { msgSucesso, msgErro, msgAlerta } = useToastContext();


    function validar() {
        if (!pessoa.codigoInterno) {
            setError("codigoInterno", { type: "manual", message: "Código é obrigatório." }, { shouldFocus: true });
            return false;
        }
        if (!pessoa.nome) {
            setError("nome", { type: "manual", message: "Nome é obrigatório." }, { shouldFocus: true });
            return false;
        }
        if (!pessoa.tipo) {
            setError("tipo", { type: "manual", message: "Tipo é obrigatório." }, { shouldFocus: true });
            return false;
        }
        if (!pessoa.tipoDocumento) {
            setError("tipoDocumento", { type: "manual", message: "Tipo de documento é obrigatório." }, { shouldFocus: true });
            return false;
        }
        if (!pessoa.numeroDocumento) {
            if (pessoa.tipoDocumento === 'CPF') {
                setError("cpf", { type: "manual", message: "CPF é obrigatório." }, { shouldFocus: true });
            } else {
                setError("cnpj", { type: "manual", message: "CNPJ é obrigatório." }, { shouldFocus: true });
            }
            return false;
        }
        return true
    }

    function validarPersonalziado() {

        if (pessoa.tipoDocumento === 'CPF' && ((!CPF.validate(pessoa.numeroDocumento)) && !(pessoa.numeroDocumento == '000.000.000-00'))) {
            setError("cpf", { type: "manual", message: "CPF inválido" }, { shouldFocus: true });
            msgAlerta('CPF', 'CPF inválido.');
            return false;
        }
        if (pessoa.tipoDocumento === 'CNPJ' && ((!validate(pessoa.numeroDocumento)) && !(pessoa.numeroDocumento == '00.000.000/0000-00'))) {
            setError("cnpj", { type: "manual", message: "CNPJ inválido" }, { shouldFocus: true });
            msgAlerta('CNPJ', 'CNPJ inválido.');
            return false;
        }
        if ((pessoa.enderecos ?? []).some(endereco =>
            !endereco.tipoEndereco || !endereco.logradouro.trim() || !endereco.bairro.trim() || !endereco.uf.trim())) {
            msgAlerta('Endereços', 'Informe tipo, logradouro, bairro e UF de cada endereço.');
            return false;
        }
        return true;
    }

    function alterarEndereco(index: number, changes: Partial<PessoaEndereco>) {
        const enderecos = [...(pessoa.enderecos ?? [])];
        enderecos[index] = { ...enderecos[index], ...changes };
        setPessoa({ ...pessoa, enderecos });
    }

    function adicionarEndereco() {
        setPessoa({ ...pessoa, enderecos: [...(pessoa.enderecos ?? []), new PessoaEndereco()] });
    }

    function removerEndereco(index: number) {
        setPessoa({ ...pessoa, enderecos: (pessoa.enderecos ?? []).filter((_, itemIndex) => itemIndex !== index) });
    }

    const codigoKeyDown = (e: any) => {
        if (e.key === 'Enter') {
            if (!pessoa.codigoInterno) {
                pessoaService.proximoCodigo().then((data) => {
                    setPessoa({ ...pessoa, codigoInterno: parseInt(data) });
                    setFocus('nome');
                    clearErrors("codigoInterno");
                }).catch((error) => {
                    console.log(error);
                });
            }
        }
    }

    return (

        <BaseCadastro model={pessoa} service={pessoaService} setModel={(value: Pessoa) => setPessoa(value)} novoCadastro getNewModel={() => new Pessoa()}
            validar={() => validar()} validarPersonalizado={() => validarPersonalziado()} urlService="/pessoa" pathCadastro="/pessoa" tituloPagina={'Pessoa'} onSave={props.onSave} isModal={props.isModal}>

            {titulo ?
                <div className="row">
                    <Label style={{ fontSize: 18, fontWeight: 'bold' }} className="col col-sm-12">{titulo}</Label>
                </div>
                : null}

            <TabView renderActiveOnly={false}>
                <TabPanel header="Dados Gerais">
                    <div className="row">
                        <Label className="col-sm-12" obrigatorio>Código</Label>
                        <div className="col-sm-12">
                            <Controller name="codigoInterno" control={control} render={({ field }) => (
                                <InputNumber required
                                    inputRef={field.ref}
                                    className={errors.codigoInterno ? "p-invalid" : ""}
                                    onKeyDown={codigoKeyDown}
                                    value={pessoa.codigoInterno}
                                    onChange={(e) => setPessoa({ ...pessoa, codigoInterno: e.value as number })}
                                    style={{ width: '30%' }}
                                    locale="pt-BR"
                                    useGrouping={false}
                                    maxFractionDigits={0}
                                />
                            )} />
                        </div>
                        {errors.codigoInterno && errors.codigoInterno.message ? <span className='col-sm-12' style={{ color: "red" }} >{errors.codigoInterno?.message as string}</span> : null}
                    </div>

                    <div className="row">
                        <Label className="col col-sm-12" obrigatorio>Nome</Label>
                        <div className="col-sm-12">
                            <InputText className={errors.nome ? "p-invalid" : ""} value={pessoa.nome} style={{ width: '100%' }} {...register("nome", {
                                onChange: (e) => {
                                    clearErrors("nome")
                                    setPessoa({ ...pessoa, nome: e.target.value })
                                }
                            })} />
                        </div>
                        {errors.nome && errors.nome.message ? <span className='col-sm-12' style={{ color: "red" }} >{errors.nome?.message as string}</span> : null}
                    </div>

                    <div className="row">
                        <Label className="col col-sm-12" obrigatorio>Tipo</Label>
                        <div className="col-sm-12">
                            <Controller name="tipo" control={control} render={({ field }) => (
                                <Dropdown
                                    focusInputRef={field.ref}
                                    name={field.name}
                                    className={errors.tipo ? "p-invalid" : ""}
                                    value={pessoa.tipo}
                                    options={pessoaService.getTiposPessoa()}
                                    optionLabel="label" optionValue="value"
                                    style={{ width: '30%' }}
                                    onChange={(e) => {
                                        clearErrors("tipo")
                                        setPessoa({ ...pessoa, tipo: e.target.value })
                                    }}
                                />
                            )} />

                        </div>
                        {errors.tipo && errors.tipo.message ? <span className='col-sm-12' style={{ color: "red" }} >{errors.tipo?.message as string}</span> : null}
                    </div>

                    <div className="row">
                        <Label className="col col-sm-12" obrigatorio>Tipo de Documento</Label>
                        <div className="col-sm-12">
                            <Controller name="tipoDocumento" control={control} render={({ field }) => (
                                <Dropdown className={errors.tipoDocumento ? "p-invalid" : ""} value={pessoa.tipoDocumento} options={pessoaService.getTiposDocumento()} optionLabel="label" optionValue="value"
                                    focusInputRef={field.ref}
                                    style={{ width: '30%' }}
                                    onChange={(e) => {
                                        clearErrors("tipoDocumento")
                                        setPessoa({ ...pessoa, tipoDocumento: e.target.value, numeroDocumento: "" })
                                    }}
                                />
                            )} />
                        </div>
                        {errors.tipoDocumento && errors.tipoDocumento.message ? <span className='col-sm-12' style={{ color: "red" }} >{errors.tipoDocumento?.message as string}</span> : null}
                    </div>

                    {pessoa.tipoDocumento === 'CPF' ?
                        <div className="row">
                            <Label className="col col-sm-12" obrigatorio>CPF</Label>
                            <div className="col-sm-12">
                                <InputText className={errors.cpf ? "p-invalid" : ""} value={pessoa.numeroDocumento}
                                    style={pessoa.numeroDocumento ?
                                        cpfMask(pessoa.numeroDocumento).length == 14 ?
                                            CPF.validate(pessoa.numeroDocumento) || pessoa.numeroDocumento == '000.000.000-00' ?
                                                { color: "#22211e", width: '30%' } :
                                                { color: "red", width: '30%' } :
                                            { width: '30%' } :
                                        { color: "red", width: '30%' }
                                    }
                                    {...register("cpf", {
                                        onChange: (e) => {
                                            clearErrors("cpf")
                                            setPessoa({ ...pessoa, numeroDocumento: cpfMask(e.target.value) })
                                        }
                                    })}
                                />

                            </div>
                            {errors.cpf && errors.cpf.message ? <span className='col-sm-12' style={{ color: "red" }} >{errors.cpf?.message as string}</span> : null}
                        </div>
                        : pessoa.tipoDocumento === 'CNPJ' ?
                            <div className="row">
                                <Label className="col col-sm-12" obrigatorio>CNPJ</Label>
                                <div className="col-sm-12">
                                    <InputText className={errors.cnpj ? "p-invalid" : ""} value={pessoa.numeroDocumento}
                                        style={pessoa.numeroDocumento ?
                                            cnpjMask(pessoa.numeroDocumento).length == 18 ?
                                                validate(cnpjMask(pessoa.numeroDocumento)) || pessoa.numeroDocumento == '00.000.000/0000-00' ?
                                                    { color: "#22211e", width: '30%' } :
                                                    { color: "red", width: '30%' } :
                                                { width: '30%' } :
                                            { width: '30%' }}
                                        {...register("cnpj", {
                                            onChange: (e) => {
                                                clearErrors("cnpj")
                                                setPessoa({ ...pessoa, numeroDocumento: cnpjMask(e.target.value) });
                                            }
                                        })}

                                    />

                                </div>
                                {errors.cnpj && errors.cnpj.message ? <span className='col-sm-12' style={{ color: "red" }} >{errors.cnpj?.message as string}</span> : null}
                            </div>
                            : null}

                    {props.isModal ? null : <div className="row">
                        <div className="col-sm-12">
                            <DataTable
                                emptyMessage="Nenhum registro encontrado"
                                header="Usuários"
                                dataKey="id"
                                value={pessoa.usuarios ? pessoa.usuarios : []}
                                scrollable
                                scrollHeight='500px'
                            >
                                <Column field="nome" header="Nome" />
                                <Column field="email" header="Nome" />
                            </DataTable>
                        </div>
                    </div>}
                </TabPanel>
                <TabPanel header="Dados Fiscais">
                    <div className="row">
                        <Label className="col-sm-12">Nome Fantasia</Label>
                        <div className="col-sm-12">
                            <InputText value={pessoa.nomeFantasia ?? ""} maxLength={60}
                                onChange={(e) => setPessoa({ ...pessoa, nomeFantasia: e.target.value || null })}
                                style={{ width: '50%' }} />
                        </div>
                    </div>
                    <div className="row">
                        <Label className="col-sm-12">Indicador de Inscrição Estadual</Label>
                        <div className="col-sm-12">
                            <Dropdown value={pessoa.indicadorInscricaoEstadual}
                                options={pessoaService.getIndicadoresInscricaoEstadual()}
                                optionLabel="label" optionValue="value" showClear
                                onChange={(e) => setPessoa({ ...pessoa, indicadorInscricaoEstadual: e.value })}
                                style={{ width: '50%' }} />
                        </div>
                    </div>
                    <div className="row">
                        <Label className="col-sm-12">Inscrição Estadual</Label>
                        <div className="col-sm-12">
                            <InputText value={pessoa.inscricaoEstadual ?? ""} maxLength={20}
                                onChange={(e) => setPessoa({ ...pessoa, inscricaoEstadual: e.target.value || null })}
                                style={{ width: '50%' }} />
                        </div>
                    </div>
                    <div className="row">
                        <Label className="col-sm-12">E-mail Fiscal</Label>
                        <div className="col-sm-12">
                            <InputText value={pessoa.emailFiscal ?? ""} maxLength={254}
                                onChange={(e) => setPessoa({ ...pessoa, emailFiscal: e.target.value || null })}
                                style={{ width: '50%' }} />
                        </div>
                    </div>
                    <div className="row">
                        <Label className="col-sm-12">Identificação Estrangeira</Label>
                        <div className="col-sm-12">
                            <InputText value={pessoa.identificacaoEstrangeira ?? ""} maxLength={20}
                                onChange={(e) => setPessoa({ ...pessoa, identificacaoEstrangeira: e.target.value || null })}
                                style={{ width: '50%' }} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-sm-12">
                            <Checkbox inputId="consumidorFinalPadrao" checked={!!pessoa.consumidorFinalPadrao}
                                onChange={(e) => setPessoa({ ...pessoa, consumidorFinalPadrao: !!e.checked })} />
                            <label htmlFor="consumidorFinalPadrao" style={{ marginLeft: 8 }}>Consumidor final por padrão</label>
                        </div>
                    </div>
                </TabPanel>
                <TabPanel header="Endereços">
                    {(pessoa.enderecos ?? []).map((endereco, index) => (
                        <div key={endereco.id ?? `novo-${index}`} className="mb-4 p-3 border-round border-1 surface-border">
                            <div className="flex justify-content-between align-items-center mb-3">
                                <strong>Endereço {index + 1}</strong>
                                <Button type="button" icon="pi pi-trash" className="p-button-danger p-button-text"
                                    aria-label="Remover endereço" onClick={() => removerEndereco(index)} />
                            </div>
                            <div className="row">
                                <Label className="col-sm-12" obrigatorio>Tipo de Endereço</Label>
                                <div className="col-sm-12">
                                    <Dropdown value={endereco.tipoEndereco} options={pessoaService.getTiposEnderecoFiscal()}
                                        optionLabel="label" optionValue="value"
                                        onChange={(e) => alterarEndereco(index, { tipoEndereco: e.value })}
                                        style={{ width: '50%' }} />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-sm-12">
                                    <Checkbox inputId={`endereco-principal-${index}`} checked={endereco.principal}
                                        onChange={(e) => alterarEndereco(index, { principal: !!e.checked })} />
                                    <label htmlFor={`endereco-principal-${index}`} style={{ marginLeft: 8 }}>Principal</label>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-sm-12">
                                    <Checkbox inputId={`endereco-ativo-${index}`} checked={endereco.ativo}
                                        onChange={(e) => alterarEndereco(index, { ativo: !!e.checked })} />
                                    <label htmlFor={`endereco-ativo-${index}`} style={{ marginLeft: 8 }}>Ativo</label>
                                </div>
                            </div>
                            <div className="row">
                                <Label className="col-sm-12" obrigatorio>Logradouro</Label>
                                <div className="col-sm-12">
                                    <InputText value={endereco.logradouro} maxLength={60}
                                        onChange={(e) => alterarEndereco(index, { logradouro: e.target.value })} style={{ width: '50%' }} />
                                </div>
                            </div>
                            <div className="row">
                                <Label className="col-sm-12">Número</Label>
                                <div className="col-sm-12">
                                    <InputText value={endereco.numeroEndereco ?? ""} maxLength={60}
                                        onChange={(e) => alterarEndereco(index, { numeroEndereco: e.target.value || null })} style={{ width: '50%' }} />
                                </div>
                            </div>
                            <div className="row">
                                <Label className="col-sm-12">Complemento</Label>
                                <div className="col-sm-12">
                                    <InputText value={endereco.complemento ?? ""} maxLength={60}
                                        onChange={(e) => alterarEndereco(index, { complemento: e.target.value || null })} style={{ width: '50%' }} />
                                </div>
                            </div>
                            <div className="row">
                                <Label className="col-sm-12" obrigatorio>Bairro</Label>
                                <div className="col-sm-12">
                                    <InputText value={endereco.bairro} maxLength={60}
                                        onChange={(e) => alterarEndereco(index, { bairro: e.target.value })} style={{ width: '50%' }} />
                                </div>
                            </div>
                            <div className="row">
                                <Label className="col-sm-12">Município</Label>
                                <div className="col-sm-12">
                                    <InputText value={endereco.municipio ?? ""} maxLength={60}
                                        onChange={(e) => alterarEndereco(index, { municipio: e.target.value || null })} style={{ width: '50%' }} />
                                </div>
                            </div>
                            <div className="row">
                                <Label className="col-sm-12">Código IBGE do Município</Label>
                                <div className="col-sm-12">
                                    <InputText value={endereco.codigoMunicipioIbge ?? ""} maxLength={7}
                                        onChange={(e) => alterarEndereco(index, { codigoMunicipioIbge: e.target.value || null })} style={{ width: '50%' }} />
                                </div>
                            </div>
                            <div className="row">
                                <Label className="col-sm-12" obrigatorio>UF</Label>
                                <div className="col-sm-12">
                                    <InputText value={endereco.uf} maxLength={2}
                                        onChange={(e) => alterarEndereco(index, { uf: e.target.value.toUpperCase() })} style={{ width: '50%' }} />
                                </div>
                            </div>
                            <div className="row">
                                <Label className="col-sm-12">CEP</Label>
                                <div className="col-sm-12">
                                    <InputText value={cepMask(endereco.cep ?? "")} maxLength={9}
                                        onChange={(e) => alterarEndereco(index, { cep: e.target.value.replace(/\D/g, '').slice(0, 8) || null })} style={{ width: '50%' }} />
                                </div>
                            </div>
                            <div className="row">
                                <Label className="col-sm-12">Telefone</Label>
                                <div className="col-sm-12">
                                    <InputText value={phoneMask(endereco.telefone ?? "")} maxLength={15}
                                        onChange={(e) => alterarEndereco(index, { telefone: e.target.value.replace(/\D/g, '').slice(0, 11) || null })} style={{ width: '50%' }} />
                                </div>
                            </div>
                            <div className="row">
                                <Label className="col-sm-12">Código BACEN do País</Label>
                                <div className="col-sm-12">
                                    <InputText value={endereco.codigoPaisBacen ?? ""} maxLength={4}
                                        onChange={(e) => alterarEndereco(index, { codigoPaisBacen: e.target.value || null })} style={{ width: '50%' }} />
                                </div>
                            </div>
                            <div className="row">
                                <Label className="col-sm-12">País</Label>
                                <div className="col-sm-12">
                                    <InputText value={endereco.pais ?? ""} maxLength={60}
                                        onChange={(e) => alterarEndereco(index, { pais: e.target.value || null })} style={{ width: '50%' }} />
                                </div>
                            </div>
                        </div>
                    ))}
                    <Button type="button" label="Adicionar endereço" icon="pi pi-plus" onClick={adicionarEndereco} />
                </TabPanel>
            </TabView>
        </BaseCadastro>
    )
}
